import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { X, Coins, Sparkles, Minus } from "lucide-react";

/**
 * Quick-action modal for a single kid. Mirrors the "Quick actions" section
 * of the parent iOS app so the web dashboard has parity: pause/resume,
 * override (allow outside schedule), take-5-off, and token award.
 *
 * Kept intentionally compact — the full detail flows still live on
 * /kids/:id (KidDetail page).
 */
export default function KidQuickActionsModal({ kid, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [tokenTypes, setTokenTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [tokenCount, setTokenCount] = useState(1);
  const [tokenNote, setTokenNote] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tokens");
        setTokenTypes(data);
        if (data.length) setSelectedType(data[0].id);
      } catch (e) {
        // Token types are optional — dashboard still works without them.
      }
    })();
  }, []);

  async function run(fn, msg) {
    setBusy(true); setStatus(null);
    try {
      await fn();
      setStatus(msg);
      onChanged?.();
    } catch (e) {
      setStatus(`Failed: ${formatApiErrorDetail(e?.response?.data?.detail)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6"
         onClick={onClose} data-testid={`kid-quick-modal-${kid.id}`}>
      <div className="card-soft p-6 md:p-7 w-full max-w-md fade-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-semibold text-[#2C342A]">{kid.name}</h3>
          <button onClick={onClose} className="text-[#707A6E] hover:text-[#2C342A]" data-testid="kid-quick-close">
            <X size={20} />
          </button>
        </div>

        {/* Allow outside schedule */}
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E] mb-2">
            <Sparkles size={12} className="inline mr-1" /> Allow outside schedule
          </div>
          {kid.policy?.override_active ? (
            <div className="flex items-center justify-between rounded-xl bg-[#E6EDE1] p-3">
              <span className="text-sm text-[#2C342A]">
                Override active — {Math.round((kid.policy.override_remaining_seconds || 0) / 60)} min left
              </span>
              <button data-testid={`kid-quick-override-clear-${kid.id}`}
                      disabled={busy}
                      onClick={() => run(
                        () => api.post(`/kids/${kid.id}/override/clear`),
                        "Override cleared"
                      )}
                      className="text-xs font-semibold text-[#C06E52] hover:underline">Clear</button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {[15, 30, 60].map((m) => (
                <button key={m} data-testid={`kid-quick-override-${m}-${kid.id}`} disabled={busy}
                        onClick={() => run(
                          () => api.post(`/kids/${kid.id}/override`, { minutes: m }),
                          `Allowed ${m} min`
                        )}
                        className="rounded-xl bg-[#E6EDE1] px-3 py-2 text-sm font-semibold text-[#4B6640] hover:bg-[#E6EDE1] transition-colors">
                  {m} min
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Take time off */}
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E] mb-2">
            <Minus size={12} className="inline mr-1" /> Take time off
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[5, 10, 15].map((m) => (
              <button key={m} data-testid={`kid-quick-reduce-${m}-${kid.id}`} disabled={busy}
                      onClick={() => run(
                        () => api.post("/reductions", {
                          kid_ids: [kid.id], minutes: m, reason: "Quick action", category: ""
                        }),
                        `-${m} min applied`
                      )}
                      className="rounded-xl bg-[#F6E3DC] px-3 py-2 text-sm font-semibold text-[#A35A42] hover:bg-[#EFC9BF] transition-colors">
                −{m} min
              </button>
            ))}
          </div>
        </div>

        {/* Award tokens */}
        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E] mb-2">
            <Coins size={12} className="inline mr-1" /> Award tokens
          </div>
          {tokenTypes.length === 0 ? (
            <div className="text-sm text-[#707A6E] italic">
              No token types set up yet. Go to Family setup → Tokens to create some.
            </div>
          ) : (
            <div className="space-y-2">
              <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}
                      data-testid={`kid-quick-token-type-${kid.id}`}
                      className="w-full rounded-xl border border-[#E4E2DC] bg-white px-3 py-2 text-sm outline-none focus:border-[#5C7A4F]">
                {tokenTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.dollar_value ? `($${t.dollar_value})` : ""}
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <input type="number" min="1" step="1" value={tokenCount}
                       data-testid={`kid-quick-token-count-${kid.id}`}
                       onChange={(e) => setTokenCount(parseInt(e.target.value) || 1)}
                       className="w-20 rounded-xl border border-[#E4E2DC] bg-white px-3 py-2 text-sm outline-none focus:border-[#5C7A4F]" />
                <input placeholder="Note (optional)" value={tokenNote}
                       data-testid={`kid-quick-token-note-${kid.id}`}
                       onChange={(e) => setTokenNote(e.target.value)}
                       className="flex-1 rounded-xl border border-[#E4E2DC] bg-white px-3 py-2 text-sm outline-none focus:border-[#5C7A4F]" />
              </div>
              <button data-testid={`kid-quick-token-award-${kid.id}`}
                      disabled={busy || !selectedType || tokenCount < 1}
                      onClick={() => run(
                        () => api.post("/tokens/award", {
                          kid_id: kid.id,
                          token_type_id: selectedType,
                          count: tokenCount,
                          note: tokenNote || undefined,
                        }),
                        `Awarded ${tokenCount} token(s)`
                      )}
                      className="w-full rounded-xl bg-[#F2B544] px-4 py-2 text-sm font-semibold text-[#5A3F0A] hover:bg-[#E0A33A] disabled:opacity-50 transition-colors">
                Award {tokenCount} token{tokenCount > 1 ? "s" : ""}
              </button>
            </div>
          )}
        </div>

        {status && (
          <div className="mt-4 text-sm text-[#2C342A]" data-testid={`kid-quick-status-${kid.id}`}>
            {status}
          </div>
        )}
      </div>
    </div>
  );
}
